import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Send, Check, Pencil, Trash2, X, Users, MapPin, Calendar, Clock, ThumbsUp, ThumbsDown, Ban } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAirline } from '../../utils/airlines';
import { getCityName } from '../../utils/airports';
import { formatDateShort } from '../../utils/dates';
import { haptic, hapticMedium } from '../../utils/haptics';
import { AvatarImg } from '../shared/Avatar';
import { useKeyboard } from '../../hooks/useKeyboard';
import type { GroupMessage, User, MeetupProposalMetadata, MeetupRsvp } from '../../types';

interface GroupChatViewProps {
  groupId: string;
  onClose: () => void;
}

function formatMessageDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDate.getTime() === today.getTime()) return 'Today';
  if (msgDate.getTime() === yesterday.getTime()) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatTime12(t: string): string {
  const [h] = t.split(':').map(Number);
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

function MeetupProposalCard({
  metadata,
  currentUserId,
  usersById,
  meetupRsvps,
  meetupStatus,
  onVote,
  onCancel,
}: {
  metadata: MeetupProposalMetadata;
  currentUserId: string;
  usersById: Map<string, User>;
  meetupRsvps?: MeetupRsvp[];
  meetupStatus?: 'proposed' | 'confirmed' | 'cancelled';
  onVote: (vote: 'yes' | 'no') => void;
  onCancel?: () => void;
}) {
  // Prefer meetup_rsvps table data if available, fall back to metadata.votes
  const rsvps = meetupRsvps && meetupRsvps.length > 0 ? meetupRsvps : null;
  const myVote = rsvps
    ? (rsvps.find((r) => r.userId === currentUserId)?.status === 'pending' ? undefined : rsvps.find((r) => r.userId === currentUserId)?.status)
    : metadata.votes[currentUserId];
  const yesVoters: [string, string][] = rsvps
    ? rsvps.filter((r) => r.status === 'yes').map((r) => [r.userId, 'yes'])
    : Object.entries(metadata.votes).filter(([, v]) => v === 'yes');
  const noVoters: [string, string][] = rsvps
    ? rsvps.filter((r) => r.status === 'no').map((r) => [r.userId, 'no'])
    : Object.entries(metadata.votes).filter(([, v]) => v === 'no');
  const pendingCount = rsvps ? rsvps.filter((r) => r.status === 'pending').length : 0;

  return (
    <div className="bg-slate-700/80 rounded-xl p-3 border border-slate-600/50 max-w-[280px]">
      {/* City + date + time */}
      <div className="flex items-center gap-2 mb-2">
        <MapPin size={14} className="text-coral shrink-0" />
        <span className="text-sm font-bold text-slate-100">{getCityName(metadata.city)}</span>
        <span className="text-xs text-slate-500 font-mono">{metadata.city}</span>
      </div>
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-1">
          <Calendar size={12} className="text-slate-400" />
          <span className="text-xs text-slate-200">{formatDateShort(metadata.date)}</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock size={12} className="text-slate-400" />
          <span className="text-xs text-slate-200">{formatTime12(metadata.time)}</span>
        </div>
      </div>

      {/* Cancelled state */}
      {meetupStatus === 'cancelled' ? (
        <div className="flex items-center gap-1.5 py-2 text-xs text-slate-500 italic">
          <Ban size={12} />
          <span>This meetup was cancelled</span>
        </div>
      ) : (
        <>
          {/* Vote buttons */}
          <div className="flex gap-2 mb-2">
            <button
              onClick={() => { haptic(); onVote('yes'); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all active:scale-95 ${
                myVote === 'yes'
                  ? 'bg-sage/30 text-sage border border-sage/40'
                  : 'bg-slate-600/60 text-slate-300 border border-transparent'
              }`}
            >
              <ThumbsUp size={12} />
              Yes {yesVoters.length > 0 && <span className="text-[10px] opacity-70">({yesVoters.length})</span>}
            </button>
            <button
              onClick={() => { haptic(); onVote('no'); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all active:scale-95 ${
                myVote === 'no'
                  ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                  : 'bg-slate-600/60 text-slate-300 border border-transparent'
              }`}
            >
              <ThumbsDown size={12} />
              No {noVoters.length > 0 && <span className="text-[10px] opacity-70">({noVoters.length})</span>}
            </button>
          </div>

          {/* Vote tally */}
          {(yesVoters.length > 0 || noVoters.length > 0 || pendingCount > 0) && (
            <div className="text-[10px] text-slate-400 space-y-0.5">
              {yesVoters.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  <ThumbsUp size={9} className="text-sage shrink-0" />
                  {yesVoters.map(([uid]) => {
                    const u = usersById.get(uid);
                    return <span key={uid} className="text-slate-300">{u?.name.split(' ')[0] ?? 'Unknown'}</span>;
                  })}
                </div>
              )}
              {noVoters.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  <ThumbsDown size={9} className="text-red-400 shrink-0" />
                  {noVoters.map(([uid]) => {
                    const u = usersById.get(uid);
                    return <span key={uid} className="text-slate-300">{u?.name.split(' ')[0] ?? 'Unknown'}</span>;
                  })}
                </div>
              )}
              {pendingCount > 0 && (
                <div className="text-slate-500">{pendingCount} pending</div>
              )}
            </div>
          )}

          {/* Cancel button — visible to any group member */}
          {onCancel && (
            <button
              onClick={() => { hapticMedium(); onCancel(); }}
              className="mt-2 flex items-center justify-center gap-1 w-full py-1.5 rounded-lg text-[11px] font-medium text-red-400 bg-red-500/10 border border-red-500/20 active:scale-95 transition-all"
            >
              <Ban size={10} />
              Cancel Meetup
            </button>
          )}
        </>
      )}
    </div>
  );
}

export function GroupChatView({ groupId, onClose }: GroupChatViewProps) {
  const {
    groupChats, groupMembers, groupMessages, currentUser, usersById,
    sendGroupMessage, editGroupMessage, deleteGroupMessage, renameGroupChat,
    voteOnProposal, meetups, meetupRsvps, rsvpMeetup, cancelMeetup,
  } = useApp();

  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [activeMsg, setActiveMsg] = useState<string | null>(null);
  const [editingMsg, setEditingMsg] = useState<{ id: string; content: string } | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { keyboardHeight, isVisible: kbVisible } = useKeyboard();

  const group = groupChats.find((gc) => gc.id === groupId);
  const members = useMemo(
    () => groupMembers.filter((gm) => gm.groupId === groupId),
    [groupMembers, groupId],
  );
  const memberUsers = useMemo(
    () => members.map((m) => usersById.get(m.userId)).filter(Boolean) as User[],
    [members, usersById],
  );
  // Only show messages from after the user's joinedAt (hides history from before rejoin)
  const myMembership = members.find((m) => m.userId === currentUser.id);
  const msgs = useMemo(() => {
    const joinedAt = myMembership?.joinedAt;
    return groupMessages.filter((m) =>
      m.groupId === groupId && (!joinedAt || m.createdAt >= joinedAt),
    );
  }, [groupMessages, groupId, myMembership?.joinedAt]);

  // Mark group as read via localStorage
  useEffect(() => {
    if (msgs.length > 0) {
      const lastTs = msgs[msgs.length - 1].createdAt;
      localStorage.setItem(`rostfound_group_read_${groupId}`, lastTs);
    }
  }, [msgs, groupId]);

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs.length]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 96) + 'px';
  }, [text]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setText('');
    try {
      await sendGroupMessage(groupId, trimmed);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (editingMsg) handleEditSave();
      else handleSend();
    }
  };

  const startLongPress = useCallback((msgId: string) => {
    longPressTimer.current = setTimeout(() => {
      hapticMedium();
      setActiveMsg(msgId);
    }, 500);
  }, []);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleEdit = (msg: GroupMessage) => {
    setActiveMsg(null);
    setEditingMsg({ id: msg.id, content: msg.content });
    setText(msg.content);
    textareaRef.current?.focus();
  };

  const handleEditSave = async () => {
    if (!editingMsg) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    await editGroupMessage(editingMsg.id, trimmed);
    setEditingMsg(null);
    setText('');
  };

  const handleEditCancel = () => {
    setEditingMsg(null);
    setText('');
  };

  const handleDelete = async (msgId: string) => {
    setActiveMsg(null);
    hapticMedium();
    await deleteGroupMessage(msgId);
  };

  const handleRenameTap = () => {
    if (!group) return;
    haptic();
    setNameInput(group.name);
    setEditingName(true);
  };

  const handleRenameSave = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || !group || trimmed === group.name) {
      setEditingName(false);
      return;
    }
    await renameGroupChat(groupId, trimmed);
    setEditingName(false);
  };

  // Cluster messages by sender + time proximity
  const clusters = useMemo(() => {
    const result: { date: string; groups: { senderId: string; messages: GroupMessage[] }[] }[] = [];
    let currentDate = '';
    let currentGroup: { senderId: string; messages: GroupMessage[] } | null = null;

    for (const msg of msgs) {
      const d = formatMessageDate(msg.createdAt);
      if (d !== currentDate) {
        currentDate = d;
        currentGroup = { senderId: msg.senderId, messages: [msg] };
        result.push({ date: d, groups: [currentGroup] });
      } else {
        const lastDateEntry = result[result.length - 1];
        if (
          currentGroup &&
          currentGroup.senderId === msg.senderId &&
          new Date(msg.createdAt).getTime() - new Date(currentGroup.messages[currentGroup.messages.length - 1].createdAt).getTime() < 120000
        ) {
          currentGroup.messages.push(msg);
        } else {
          currentGroup = { senderId: msg.senderId, messages: [msg] };
          lastDateEntry.groups.push(currentGroup);
        }
      }
    }
    return result;
  }, [msgs]);

  // Scroll to bottom when keyboard appears
  useEffect(() => {
    if (kbVisible) {
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ block: 'end' });
      });
    }
  }, [kbVisible]);

  if (!group) return null;

  const portalTarget = document.getElementById('app-shell') || document.body;

  return createPortal(
    <div
      className="absolute inset-0 z-[100] flex flex-col bg-slate-900"
      style={kbVisible ? { paddingBottom: `${keyboardHeight}px` } : undefined}
    >
      {/* Header */}
      <div className="shrink-0 bg-slate-800 border-b border-slate-700/50" style={{ paddingTop: 'var(--sat)' }}>
        <div className="flex items-center gap-3 px-3 py-2.5">
          <button
            onClick={onClose}
            className="p-1.5 -ml-1 rounded-lg hover:bg-slate-700/50 active:bg-slate-600/50 transition-colors"
          >
            <ArrowLeft size={22} className="text-slate-200" />
          </button>
          <button
            onClick={handleRenameTap}
            className="flex items-center gap-3 min-w-0 flex-1 active:opacity-70 transition-opacity"
          >
            {/* Stacked member avatars */}
            <div className="flex -space-x-2 shrink-0">
              {memberUsers.slice(0, 3).map((user) => {
                const airline = getAirline(user.airlineCode);
                return (
                  <div
                    key={user.id}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-slate-800 overflow-hidden"
                    style={{ backgroundColor: airline.color, color: airline.textOnColor }}
                  >
                    <AvatarImg src={user.avatarUrl} name={user.name} />
                  </div>
                );
              })}
              {memberUsers.length > 3 && (
                <div className="w-8 h-8 rounded-full bg-slate-600 border-2 border-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-300">
                  +{memberUsers.length - 3}
                </div>
              )}
            </div>
            <div className="min-w-0 text-left">
              <div className="text-[15px] font-semibold text-slate-100 truncate">{group.name}</div>
              <div className="text-xs text-slate-500">{memberUsers.length} members</div>
            </div>
          </button>
        </div>
      </div>

      {/* Rename modal */}
      {editingName && (
        <div className="absolute inset-0 z-[110] flex items-center justify-center bg-black/60" onClick={() => setEditingName(false)}>
          <div className="bg-slate-800 rounded-2xl p-5 mx-6 w-full max-w-sm border border-slate-700" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-100 mb-3">Rename Group</h3>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              maxLength={200}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSave(); }}
              className="w-full bg-slate-700 text-slate-100 placeholder-slate-500 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-accent/40 mb-4"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditingName(false)} className="px-4 py-2 text-sm text-slate-400 rounded-lg hover:bg-slate-700">Cancel</button>
              <button onClick={handleRenameSave} className="px-4 py-2 text-sm font-semibold text-white bg-accent rounded-lg active:scale-95">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3" onClick={() => activeMsg && setActiveMsg(null)}>
        {msgs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-500">
            <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mb-3">
              <Users size={24} className="text-slate-500" />
            </div>
            <p className="text-sm font-medium">No messages yet</p>
            <p className="text-xs mt-1 text-slate-600">Start the conversation!</p>
          </div>
        ) : (
        <div className="min-h-full flex flex-col justify-end">
        {clusters.map((dateEntry) => (
          <div key={dateEntry.date}>
            <div className="flex items-center justify-center my-4">
              <span className="text-[11px] font-medium text-slate-500 bg-slate-800 px-3 py-1 rounded-full">
                {dateEntry.date}
              </span>
            </div>
            {dateEntry.groups.map((grp, gi) => {
              const isMine = grp.senderId === currentUser.id;
              const sender = usersById.get(grp.senderId);
              const senderAirline = sender ? getAirline(sender.airlineCode) : null;
              const lastMsg = grp.messages[grp.messages.length - 1];

              // Check if this is a system message group
              const isSystemMsg = grp.messages.some((m) => m.metadata?.type === 'system_message');
              if (isSystemMsg) {
                return (
                  <div key={gi} className="flex flex-col items-center my-2 gap-1">
                    {grp.messages.map((msg) => (
                      <div key={msg.id} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800/50 rounded-full max-w-[85%]">
                        {msg.metadata?.type === 'system_message' && (msg.metadata as { subtype: string }).subtype === 'auto_decline' && (
                          <span className="text-amber-400 shrink-0">&#9888;</span>
                        )}
                        <span className="text-[11px] text-slate-400 italic text-center">{msg.content}</span>
                      </div>
                    ))}
                  </div>
                );
              }

              // Helper to get rsvps for a meetup proposal message
              const getRsvpsForMsg = (msg: GroupMessage) => {
                if (msg.metadata?.type !== 'meetup_proposal') return undefined;
                const mid = (msg.metadata as MeetupProposalMetadata).meetupId;
                return mid ? meetupRsvps.filter((r) => r.meetupId === mid) : undefined;
              };

              // Helper to get meetup status
              const getMeetupStatus = (msg: GroupMessage) => {
                if (msg.metadata?.type !== 'meetup_proposal') return undefined;
                const mid = (msg.metadata as MeetupProposalMetadata).meetupId;
                return mid ? meetups.find((m) => m.id === mid)?.status : undefined;
              };

              // Helper to get cancel handler (only if meetup exists and isn't already cancelled)
              const getCancelHandler = (msg: GroupMessage) => {
                if (msg.metadata?.type !== 'meetup_proposal') return undefined;
                const mid = (msg.metadata as MeetupProposalMetadata).meetupId;
                if (!mid) return undefined;
                const meetup = meetups.find((m) => m.id === mid);
                if (!meetup || meetup.status === 'cancelled') return undefined;
                return () => cancelMeetup(mid);
              };

              // Helper to handle vote — prefer rsvpMeetup if meetupId exists, fall back to voteOnProposal
              const handleVote = (msg: GroupMessage, vote: 'yes' | 'no') => {
                if (msg.metadata?.type === 'meetup_proposal') {
                  const mid = (msg.metadata as MeetupProposalMetadata).meetupId;
                  if (mid) {
                    rsvpMeetup(mid, vote);
                    return;
                  }
                }
                voteOnProposal(msg.id, vote);
              };

              return (
                <div key={gi} className={`flex flex-col mb-3 ${isMine ? 'items-end' : 'items-start'}`}>
                  {/* Other's messages — show avatar + name */}
                  {!isMine && sender && senderAirline && (
                    <div className="flex items-end gap-2 w-full">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 mb-5 overflow-hidden"
                        style={{ backgroundColor: senderAirline.color, color: senderAirline.textOnColor }}
                      >
                        <AvatarImg src={sender.avatarUrl} name={sender.name} className="w-full h-full object-cover rounded-full" />
                      </div>
                      <div className="flex flex-col gap-0.5 max-w-[75%]">
                        <span className="text-[11px] font-medium text-slate-400 px-1">{sender.name.split(' ')[0]}</span>
                        {grp.messages.map((msg, mi) => (
                          <div key={msg.id}>
                            <div
                              className={`px-3.5 py-2 text-[15px] leading-snug bg-slate-700 text-slate-100 shadow-sm ${
                                grp.messages.length === 1
                                  ? 'rounded-2xl rounded-bl-md'
                                  : mi === 0
                                    ? 'rounded-2xl rounded-bl-sm'
                                    : mi === grp.messages.length - 1
                                      ? 'rounded-2xl rounded-tl-sm rounded-bl-md'
                                      : 'rounded-2xl rounded-l-sm'
                              }`}
                            >
                              {msg.content}
                              {msg.editedAt && <span className="text-[10px] text-slate-400 ml-1.5">edited</span>}
                            </div>
                            {msg.metadata?.type === 'meetup_proposal' && (
                              <div className="mt-1">
                                <MeetupProposalCard
                                  metadata={msg.metadata as MeetupProposalMetadata}
                                  currentUserId={currentUser.id}
                                  usersById={usersById}
                                  meetupRsvps={getRsvpsForMsg(msg)}
                                  meetupStatus={getMeetupStatus(msg)}
                                  onVote={(vote) => handleVote(msg, vote)}
                                  onCancel={getCancelHandler(msg)}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                        <span className="text-[10px] text-slate-500 px-1 mt-0.5">{formatTime(lastMsg.createdAt)}</span>
                      </div>
                    </div>
                  )}
                  {/* Own messages */}
                  {isMine && (
                    <div className="flex flex-col items-end gap-0.5 max-w-[75%]">
                      {grp.messages.map((msg) => (
                        <div key={msg.id} className="relative">
                          <div
                            onTouchStart={() => startLongPress(msg.id)}
                            onTouchEnd={cancelLongPress}
                            onTouchCancel={cancelLongPress}
                            onContextMenu={(e) => { e.preventDefault(); hapticMedium(); setActiveMsg(msg.id); }}
                            className={`px-3.5 py-2 text-[15px] leading-snug bg-accent text-white shadow-sm select-none ${
                              activeMsg === msg.id ? 'ring-2 ring-white/30' : ''
                            } rounded-2xl rounded-br-md`}
                          >
                            {msg.content}
                            {msg.editedAt && <span className="text-[10px] text-white/50 ml-1.5">edited</span>}
                          </div>
                          {msg.metadata?.type === 'meetup_proposal' && (
                            <div className="mt-1">
                              <MeetupProposalCard
                                metadata={msg.metadata as MeetupProposalMetadata}
                                currentUserId={currentUser.id}
                                usersById={usersById}
                                meetupRsvps={getRsvpsForMsg(msg)}
                                meetupStatus={getMeetupStatus(msg)}
                                onVote={(vote) => handleVote(msg, vote)}
                                onCancel={getCancelHandler(msg)}
                              />
                            </div>
                          )}
                          {activeMsg === msg.id && (
                            <div className="absolute right-0 bottom-full mb-1 flex items-center gap-1 bg-slate-800 border border-slate-700 rounded-xl px-1 py-0.5 shadow-lg z-10 animate-fade-in">
                              <button
                                onClick={() => handleEdit(msg)}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
                              >
                                <Pencil size={12} /> Edit
                              </button>
                              <button
                                onClick={() => handleDelete(msg.id)}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              >
                                <Trash2 size={12} /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      <span className="text-[10px] text-slate-500 px-1 mt-0.5">{formatTime(lastMsg.createdAt)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        <div ref={bottomRef} />
        </div>
        )}
      </div>

      {/* Input bar */}
      <div
        className="shrink-0 bg-slate-800 border-t border-slate-700/50 px-3 py-2.5"
        style={{ paddingBottom: kbVisible ? '8px' : 'calc(var(--sab) + 8px)' }}
      >
        {editingMsg && (
          <div className="flex items-center gap-2 px-1 pb-2 text-xs">
            <Pencil size={12} className="text-accent" />
            <span className="text-accent font-medium">Editing message</span>
            <button onClick={handleEditCancel} className="ml-auto p-0.5 text-slate-500 hover:text-slate-300">
              <X size={14} />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2.5">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={editingMsg ? 'Edit message...' : 'Message...'}
            rows={1}
            className="flex-1 bg-slate-700 text-slate-100 placeholder-slate-500 rounded-2xl px-4 py-2.5 text-[15px] resize-none outline-none focus:ring-2 focus:ring-accent/40 max-h-24"
          />
          <button
            onClick={editingMsg ? handleEditSave : handleSend}
            disabled={!text.trim() || sending}
            className="p-2.5 rounded-full bg-accent text-white disabled:opacity-30 active:scale-95 transition-all shrink-0"
          >
            {editingMsg ? <Check size={18} /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </div>,
    portalTarget,
  );
}
